import { KubesoloHostCounts, KubesoloTest } from '@api/types.gen';

import { pluralize } from '@/portainer/helpers/strings';

import { Button } from '@@/buttons';
import { StackedStatusBar } from '@@/StackedStatusBar';
import { TooltipWithChildren } from '@@/Tip/TooltipWithChildren';

import { isInProgress } from '../queries/useActiveConnectionTest';

import { formatDuration, testEndMs } from './durations';
import { groupLabels } from './groupHosts';
import { useNow } from './useNow';

/** Worded like the policy status bar's breakdown, e.g. "16 machines: 13 failed, 3 passed". */
const breakdownLabels: Array<[keyof KubesoloHostCounts, string]> = [
  ['failed', 'failed'],
  ['running', 'running'],
  ['queued', 'queued'],
  ['passed', 'passed'],
  ['alreadyInstalled', 'already installed'],
];

interface Props {
  test: KubesoloTest;
  onViewDetails(): void;
}

export function ProgressHeader({ test, onViewDetails }: Props) {
  const inProgress = isInProgress(test);
  const now = useNow(inProgress);
  const total = test.hosts.length;
  const checked = total - test.counts.queued - test.counts.running;
  const startedAt = new Date(test.createdAt);

  return (
    <div className="mb-3 flex flex-col gap-2">
      <div className="flex items-center justify-between gap-4">
        <span className="text-muted small">
          Started{' '}
          {startedAt.toLocaleTimeString([], {
            hour: '2-digit',
            minute: '2-digit',
          })}{' '}
          · {formatDuration(testEndMs(test, now) - startedAt.getTime())}
        </span>

        <Button
          color="default"
          onClick={onViewDetails}
          data-cy="kubesolo-view-details-button"
        >
          View details
        </Button>
      </div>

      {/* its own parent, so the tooltip follows the bar in the DOM */}
      <div>
        <TooltipWithChildren message={breakdown(test)} position="top">
          <StackedStatusBar
            steps={[
              {
                value: test.counts.failed,
                className:
                  'bg-error-7 th-dark:bg-error-7 th-highcontrast:bg-error-7',
                label: groupLabels.attention,
              },
              {
                value: test.counts.passed,
                className:
                  'bg-success-7 th-dark:bg-success-7 th-highcontrast:bg-success-7',
                label: groupLabels.passed,
              },
              {
                value: test.counts.alreadyInstalled,
                className:
                  'bg-warning-7 th-dark:bg-warning-7 th-highcontrast:bg-warning-7',
                label: groupLabels.alreadyInstalled,
              },
            ]}
            total={total}
            aria-label="Machines checked"
          />
        </TooltipWithChildren>
      </div>
      <p className="text-muted small m-0" aria-live="polite">
        {checked} of {total} checked
        {checked === total
          ? ''
          : ` · ${test.workers} ${pluralize(test.workers, 'machine')} at a time`}
      </p>
    </div>
  );
}

function breakdown({ hosts, counts }: KubesoloTest) {
  const parts = breakdownLabels
    .filter(([state]) => counts[state] > 0)
    .map(([state, label]) => `${counts[state]} ${label}`);
  return `${hosts.length} ${pluralize(hosts.length, 'machine')}: ${parts.join(', ')}`;
}
